import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  api,
  fetchInviteMetadata,
  InviteMetadata,
  ApiError,
} from "../../lib/api";

type PageState =
  | "loading"
  | "invalid"
  | "already_used"
  | "ready"
  | "accepting"
  | "already_member"
  | "success"
  | "error";

// ─── Wrapper shell — defined outside component to avoid "created during render" lint error
function InviteWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-white flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img
            src="/images/media-dingo-logo.svg"
            alt="Media Dingo"
            className="h-24 w-auto object-contain mb-2"
          />
        </div>
        <div className="card border border-gray-200 p-8 text-center">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const { isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();

  // If no token param, start in invalid state immediately
  const [pageState, setPageState] = useState<PageState>(
    token ? "loading" : "invalid",
  );
  const [invite, setInvite] = useState<InviteMetadata | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    fetchInviteMetadata(token)
      .then((data) => {
        setInvite(data);
        setPageState(data.valid ? "ready" : "already_used");
      })
      .catch(() => setPageState("invalid"));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setPageState("accepting");
    try {
      await api.team.acceptInvite(token);
      setPageState("success");
      // Brief pause then redirect so the user sees the success message
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      if (err instanceof ApiError && err.message.includes("already a member")) {
        setPageState("already_member");
      } else {
        setErrorMessage(
          err instanceof Error ? err.message : "Something went wrong",
        );
        setPageState("error");
      }
    }
  };

  // ─── States ──────────────────────────────────────────────────────────────────

  if (!isLoaded || pageState === "loading") {
    return (
      <InviteWrap>
        <div className="flex justify-center">
          <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </InviteWrap>
    );
  }

  if (pageState === "invalid") {
    return (
      <InviteWrap>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Invite not found
        </h1>
        <p className="text-sm text-gray-500">
          This invite link is invalid or has expired. Ask your team admin to
          send a new one.
        </p>
      </InviteWrap>
    );
  }

  if (pageState === "already_used") {
    return (
      <InviteWrap>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Invite already used
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          This invite to <strong>{invite?.orgName}</strong> has already been
          accepted.
        </p>
        {isSignedIn ? (
          <button onClick={() => navigate("/")} className="btn-primary w-full">
            Go to dashboard
          </button>
        ) : (
          <a href="/login" className="btn-primary block w-full">
            Sign in
          </a>
        )}
      </InviteWrap>
    );
  }

  if (pageState === "already_member") {
    return (
      <InviteWrap>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Already a member
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          You&apos;re already a member of an organization.
        </p>
        <button onClick={() => navigate("/")} className="btn-primary w-full">
          Go to dashboard
        </button>
      </InviteWrap>
    );
  }

  if (pageState === "success") {
    return (
      <InviteWrap>
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        </div>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          You&apos;re in!
        </h1>
        <p className="text-sm text-gray-500">
          Welcome to <strong>{invite?.orgName}</strong>. Redirecting you now…
        </p>
      </InviteWrap>
    );
  }

  if (pageState === "error") {
    return (
      <InviteWrap>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-red-600 mb-6">{errorMessage}</p>
        <button
          onClick={() => setPageState("ready")}
          className="btn-primary w-full"
        >
          Try again
        </button>
      </InviteWrap>
    );
  }

  // ─── Ready to accept ────────────────────────────────────────────────────────

  return (
    <InviteWrap>
      <div className="flex justify-center mb-4">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100">
          <svg
            className="w-6 h-6 text-brand-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </span>
      </div>

      <h1 className="text-lg font-semibold text-gray-900 mb-1">
        You&apos;ve been invited
      </h1>
      <p className="text-sm text-gray-500 mb-1">
        Join <strong className="text-gray-800">{invite?.orgName}</strong> on
        Media Dingo
      </p>
      {invite?.email && (
        <p className="text-xs text-gray-400 mb-6">Invited as {invite.email}</p>
      )}

      {isSignedIn ? (
        <button
          onClick={handleAccept}
          disabled={pageState === "accepting"}
          className="btn-primary w-full"
        >
          {pageState === "accepting" ? "Joining…" : `Join ${invite?.orgName}`}
        </button>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            Sign in or create an account to accept this invite.
          </p>
          <a
            href={`/login?redirect_url=${encodeURIComponent(window.location.pathname)}`}
            className="btn-primary block w-full text-center"
          >
            Sign in to accept
          </a>
        </div>
      )}
    </InviteWrap>
  );
}
